// apps/web/src/components/RequireAuth.tsx — route guard for /admin/*.
//
// The cold-start silentRefresh() lives here (not in AuthProvider) so
// public routes never trigger a refresh probe. RequireAuth only
// wraps /admin/* (see apps/web/src/main.tsx), so the probe fires
// exactly once per /admin/* mount. Public routes skip it entirely.
//
// Per D-02: the access token is in React memory only. RequireAuth
// fires `silentRefresh()` on mount. If the user has a valid
// refresh-cookie the server returns a new accessToken; otherwise
// silentRefresh() fails silently and `handleReuseOrMissing` (wired
// in auth.tsx) navigates to /login — RequireAuth then renders
// `<Navigate to="/login">` as a no-op fallback so the redirect
// happens reliably even if the navigate event was missed.

import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "../lib/auth";
import { silentRefresh } from "../lib/api";

export function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const location = useLocation();
  const [refreshing, setRefreshing] = useState(true);

  useEffect(() => {
    let active = true;
    void silentRefresh().finally(() => {
      if (active) setRefreshing(false);
    });
    return () => {
      active = false;
    };
  }, []);

  if (refreshing) {
    return (
      <div role="status" aria-live="polite" aria-busy="true">
        Sitzung wird geprüft…
      </div>
    );
  }
  if (!state.accessToken) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}
